import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Eye, Loader2, Paperclip, X } from 'lucide-react';
import { apiClient } from '../api/client';
import { MisActionConfirm, type MisConfirmAction } from './MisActionConfirm';

const MIS_FILE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'xls', 'xlsx', 'doc', 'docx', 'pdf']);
const MIS_MAX_FILE_BYTES = 15 * 1024 * 1024;

function fileExtension(name: string) {
  const parts = name.toLowerCase().split('.');
  return parts.length > 1 ? parts[parts.length - 1] : '';
}

function ticketId(id: string) {
  return `CG${id.slice(0, 7).toUpperCase()}`;
}

export function MisDecisionModal({ ideaId, onClose }: { ideaId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [description, setDescription] = useState('');
  const [pendingFiles, setPendingFiles] = useState<{ id: string; file: File; previewUrl: string }[]>([]);
  const [removedFileIds, setRemovedFileIds] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [confirmAction, setConfirmAction] = useState<MisConfirmAction | null>(null);
  const [undoDescription, setUndoDescription] = useState('');

  const { data: idea, isLoading, isError } = useQuery({
    queryKey: ['misIdea', ideaId],
    queryFn: async () => {
      const res = await apiClient.get(`/admin/mis/ideas/${ideaId}`);
      return res.data.data;
    },
  });

  useEffect(() => {
    if (idea?.misReview?.misDescription) setDescription(idea.misReview.misDescription);
  }, [idea?.misReview?.misDescription]);

  const pendingFilesRef = useRef(pendingFiles);
  pendingFilesRef.current = pendingFiles;

  useEffect(() => {
    return () => pendingFilesRef.current.forEach((pending) => URL.revokeObjectURL(pending.previewUrl));
  }, []);

  const decisionMutation = useMutation({
    mutationFn: async ({ misStatus, undoDescription: reason }: { misStatus: MisConfirmAction; undoDescription?: string }) => {
      const files = [];
      for (const pending of pendingFiles) {
        const presignedRes = await apiClient.get('/ideas/upload-url', {
          params: { fileName: pending.file.name, contentType: pending.file.type || 'application/octet-stream' },
        });
        const { uploadUrl, fileKey } = presignedRes.data.data;
        await axios.put(uploadUrl, pending.file, { headers: { 'Content-Type': pending.file.type || 'application/octet-stream' } });
        files.push({
          fileName: pending.file.name,
          fileKey,
          fileSize: pending.file.size,
          fileType: pending.file.type || 'application/octet-stream',
        });
      }
      const res = misStatus === 'UNAPPROVE' || misStatus === 'UNREJECT'
        ? await apiClient.post(`/admin/mis/ideas/${ideaId}/decision`, { misStatus, misDescription: reason, files })
        : await apiClient.post(`/admin/mis/ideas/${ideaId}/decision`, {
          misStatus,
          misDescription: description.trim(),
          files,
        });
      for (const fileId of removedFileIds) {
        await apiClient.delete(`/admin/mis/ideas/${ideaId}/files/${fileId}`);
      }
      return res.data.data;
    },
    onSuccess: (_data, { misStatus }) => {
      pendingFiles.forEach((pending) => URL.revokeObjectURL(pending.previewUrl));
      setPendingFiles([]);
      setRemovedFileIds([]);
      setConfirmAction(null);
      setUndoDescription('');
      queryClient.invalidateQueries({ queryKey: ['misIdea', ideaId] });
      queryClient.invalidateQueries({ queryKey: ['misIdeas'] });
      queryClient.invalidateQueries({ queryKey: ['misStats'] });
      if (misStatus === 'APPROVED' || misStatus === 'REJECTED') onClose();
    },
    onError: (err: any) => {
      setError(err.response?.data?.error?.message || 'Could not save the MIS decision.');
    },
  });

  const askConfirm = (misStatus: MisConfirmAction) => {
    if (decisionMutation.isPending) return;
    if ((misStatus === 'APPROVED' || misStatus === 'REJECTED') && description.trim().length < 3) {
      setError('Enter a description of at least 3 characters.');
      return;
    }
    setError('');
    setUndoDescription('');
    setConfirmAction(misStatus);
  };

  const confirmSubmit = () => {
    if (!confirmAction || decisionMutation.isPending) return;
    if ((confirmAction === 'UNAPPROVE' || confirmAction === 'UNREJECT') && undoDescription.trim().length < 3) {
      setError('Enter a description of at least 3 characters.');
      return;
    }
    setError('');
    decisionMutation.mutate({ misStatus: confirmAction, undoDescription: undoDescription.trim() });
  };

  return (
    <>
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/20 p-6" onClick={onClose}>
      <div
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl border border-slate-200 shadow-lg p-7"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="min-w-0">
            <h3 className="text-xs uppercase font-bold tracking-wider text-teal-700">MIS Decision</h3>
            {!isLoading && idea && (
              <p className="text-sm font-semibold text-slate-800 mt-1">{ticketId(idea.id)} · {idea.title}</p>
            )}
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700 shrink-0" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-teal-600 animate-spin" />
          </div>
        ) : isError || !idea ? (
          <p className="text-sm text-slate-600">This idea is not available.</p>
        ) : (
          <div className="flex flex-col gap-4">
            {idea.misReview?.misStatus && (
              <span className={`self-start text-[11px] font-bold px-2.5 py-1 rounded-full ${idea.misReview.misStatus === 'APPROVED' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                {idea.misReview.misStatus === 'APPROVED' ? 'MIS Approved' : 'MIS Rejected'}
                {idea.misReview.decidedBy?.name ? ` · ${idea.misReview.decidedBy.name}` : ''}
              </span>
            )}
            <div>
              <label className="block text-[10px] text-slate-500 font-bold mb-1.5 uppercase">Description</label>
              <textarea
                rows={8}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Decision notes"
                className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-800 outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[10px] text-slate-500 font-bold uppercase">Attachments</label>
                <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-teal-700 bg-teal-50 border border-teal-200 px-3 py-1.5 rounded-lg cursor-pointer hover:bg-teal-100">
                  <Paperclip className="w-3.5 h-3.5" />
                  Add files
                  <input
                    type="file"
                    multiple
                    accept=".png,.jpg,.jpeg,.xls,.xlsx,.doc,.docx,.pdf"
                    className="hidden"
                    onChange={(event) => {
                      const selected = Array.from(event.target.files || []);
                      const accepted: { id: string; file: File; previewUrl: string }[] = [];
                      const rejected: string[] = [];
                      selected.forEach((selectedFile) => {
                        const extension = fileExtension(selectedFile.name);
                        if (!MIS_FILE_EXTENSIONS.has(extension)) {
                          rejected.push(`${selectedFile.name} is not allowed. Use PNG, JPG, JPEG, Excel, Word, or PDF.`);
                          return;
                        }
                        if (selectedFile.size > MIS_MAX_FILE_BYTES) {
                          rejected.push(`${selectedFile.name} is larger than 15 MB.`);
                          return;
                        }
                        accepted.push({
                          id: `${selectedFile.name}-${selectedFile.size}-${selectedFile.lastModified}`,
                          file: selectedFile,
                          previewUrl: URL.createObjectURL(selectedFile),
                        });
                      });
                      if (accepted.length > 0) setPendingFiles((current) => [...current, ...accepted]);
                      setError(rejected.join(' '));
                      event.target.value = '';
                    }}
                  />
                </label>
              </div>
              <p className="text-[10px] text-slate-400 mb-2">PNG, JPG, JPEG, Excel, Word, PDF. Max 15 MB each.</p>
              <div className="space-y-2">
                {(idea.misReview?.files || []).filter((savedFile: any) => !removedFileIds.includes(savedFile.id)).map((savedFile: any) => (
                  <div key={savedFile.id || savedFile.fileKey} className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-slate-700 truncate">{savedFile.fileName}</p>
                      <p className="text-[10px] text-slate-400">{savedFile.fileSize ? `${(savedFile.fileSize / 1024).toFixed(0)} KB` : 'Saved'}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => window.open(savedFile.downloadUrl || savedFile.fileUrl, '_blank', 'noopener,noreferrer')}
                        className="inline-flex items-center gap-1 text-[11px] font-bold text-indigo-700 bg-white border border-indigo-200 px-2.5 py-1 rounded-lg hover:bg-indigo-50 shrink-0"
                      >
                        <Eye className="w-3.5 h-3.5" /> View
                      </button>
                      <button
                        type="button"
                        onClick={() => setRemovedFileIds((current) => current.includes(savedFile.id) ? current : [...current, savedFile.id])}
                        className="text-slate-400 hover:text-red-600"
                        aria-label="Remove file"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
                {pendingFiles.map((pending) => (
                  <div key={pending.id} className="flex items-center justify-between gap-3 bg-teal-50/60 border border-teal-100 rounded-lg px-3 py-2">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-slate-700 truncate">{pending.file.name}</p>
                      <p className="text-[10px] text-slate-400">{(pending.file.size / 1024).toFixed(0)} KB · not saved yet</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => window.open(pending.previewUrl, '_blank', 'noopener,noreferrer')}
                        className="inline-flex items-center gap-1 text-[11px] font-bold text-indigo-700 bg-white border border-indigo-200 px-2.5 py-1 rounded-lg hover:bg-indigo-50"
                      >
                        <Eye className="w-3.5 h-3.5" /> View
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          URL.revokeObjectURL(pending.previewUrl);
                          setPendingFiles((current) => current.filter((item) => item.id !== pending.id));
                        }}
                        className="text-slate-400 hover:text-red-600"
                        aria-label="Remove file"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
                {(idea.misReview?.files || []).filter((savedFile: any) => !removedFileIds.includes(savedFile.id)).length === 0 && pendingFiles.length === 0 && (
                  <p className="text-xs text-slate-400">No files attached.</p>
                )}
              </div>
            </div>
            {error && <p className="text-red-500 text-xs font-semibold">{error}</p>}
            <p className="self-start text-[11px] font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">Note: Reject is considered a duplicate.</p>
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
              {idea.misReview?.misStatus === 'APPROVED' || idea.misReview?.misStatus === 'REJECTED' ? (
                (() => {
                  const undoStatus = idea.misReview.misStatus === 'APPROVED' ? 'UNAPPROVE' : 'UNREJECT';
                  const undoLabel = idea.misReview.misStatus === 'APPROVED' ? 'Unapprove' : 'Unreject';
                  const isThisLoading = decisionMutation.isPending && decisionMutation.variables?.misStatus === undoStatus;
                  return (
                    <button
                      type="button"
                      disabled={isThisLoading}
                      onClick={() => askConfirm(undoStatus)}
                      style={{ backgroundColor: 'oklch(0.62 0.14 55)' }}
                      className="relative inline-flex items-center justify-center w-[6.75rem] h-7 rounded-lg text-white text-[10px] font-bold uppercase tracking-wide hover:brightness-95 disabled:cursor-wait"
                    >
                      <span className={isThisLoading ? 'invisible' : ''}>{undoLabel}</span>
                      {isThisLoading && <Loader2 className="absolute w-3.5 h-3.5 animate-spin" />}
                    </button>
                  );
                })()
              ) : (
                ([
                  ['REJECTED', 'Reject', 'oklch(0.62 0.18 22.45)'],
                  ['APPROVED', 'Approve', 'oklch(0.58 0.11 165.46)'],
                ] as const).map(([status, label, color]) => {
                  const isThisLoading = decisionMutation.isPending && decisionMutation.variables?.misStatus === status;
                  return (
                    <button
                      key={status}
                      type="button"
                      disabled={isThisLoading}
                      onClick={() => askConfirm(status)}
                      style={{ backgroundColor: color }}
                      className="relative inline-flex items-center justify-center w-[5.25rem] h-7 rounded-lg text-white text-[10px] font-bold uppercase tracking-wide hover:brightness-95 disabled:cursor-wait"
                    >
                      <span className={isThisLoading ? 'invisible' : ''}>{label}</span>
                      {isThisLoading && <Loader2 className="absolute w-3.5 h-3.5 animate-spin" />}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
    {confirmAction && (
      <MisActionConfirm
        action={confirmAction}
        description={undoDescription}
        onDescriptionChange={setUndoDescription}
        error={error}
        pending={decisionMutation.isPending}
        onCancel={() => {
          if (decisionMutation.isPending) return;
          setConfirmAction(null);
          setError('');
        }}
        onConfirm={confirmSubmit}
      />
    )}
    </>
  );
}
